import { Lock, Unlock } from "lucide-react";
import { Section, Segmented, SliderField, SwitchField } from "./Fields";

export const ReliefControls = ({ params, set, aspect, lockAspect, setLockAspect }) => {
  const h = params.height_mm ?? (aspect ? +(params.width_mm * aspect).toFixed(1) : params.width_mm);
  const setW = (w) => set({ width_mm: w, height_mm: lockAspect ? null : params.height_mm });
  const setH = (v) => (lockAspect && aspect ? set({ width_mm: +(v / aspect).toFixed(1), height_mm: null }) : set({ height_mm: v }));

  return (
    <div className="space-y-8">
      <Section title="Conversion">
        <Segmented
          id="relief-style"
          value={params.style}
          onChange={(v) => set({ style: v })}
          options={[
            { value: "extrude", label: "Extrude (logos)" },
            { value: "heightmap", label: "Height map (photos)" },
          ]}
        />
        <SwitchField id="invert" label="Invert" hint={params.style === "extrude" ? "Raise light areas instead of dark" : "Light areas become high"} checked={params.invert} onChange={(v) => set({ invert: v })} />
        {params.style === "extrude" && (
          <>
            <SwitchField id="auto-threshold" label="Auto threshold (Otsu)" checked={params.threshold === null} onChange={(v) => set({ threshold: v ? null : 0.5 })} />
            {params.threshold !== null && (
              <SliderField id="threshold" label="Threshold" value={params.threshold} min={0.05} max={0.95} step={0.01} onChange={(v) => set({ threshold: v })} />
            )}
          </>
        )}
        <div className="flex items-center justify-between gap-3">
          <label htmlFor="bg-color" className="text-xs text-zinc-300">
            Image background color
            <span className="block text-[11px] text-zinc-500 mt-0.5">Used behind transparent pixels</span>
          </label>
          <input id="bg-color" type="color" data-testid="bg-color-input" value={params.background_color} onChange={(e) => set({ background_color: e.target.value })} className="h-7 w-10 rounded bg-transparent border border-white/10 cursor-pointer" />
        </div>
        <SwitchField id="use-alpha" label="Transparent = empty" checked={params.use_alpha} onChange={(v) => set({ use_alpha: v })} />
      </Section>

      <Section
        title="Dimensions"
        right={
          <button type="button" data-testid="lock-aspect-btn" aria-label={lockAspect ? "Unlock aspect ratio" : "Lock aspect ratio"} onClick={() => setLockAspect(!lockAspect)} className="text-zinc-400 hover:text-white transition-colors flex items-center gap-1 text-[11px]">
            {lockAspect ? <Lock size={12} /> : <Unlock size={12} />} {lockAspect ? "Locked" : "Free"}
          </button>
        }
      >
        <SliderField id="width" label="Width" unit=" mm" value={params.width_mm} min={10} max={300} step={1} onChange={setW} />
        <SliderField id="height" label="Height" unit=" mm" value={h} min={10} max={300} step={1} onChange={setH} />
        <SliderField id="depth" label="Relief depth" unit=" mm" value={params.depth_mm} min={0.4} max={20} step={0.1} onChange={(v) => set({ depth_mm: v })} />
        <SwitchField id="add-base" label="Solid base" hint={params.add_base ? "Recommended for 3D printing" : "Off: size follows the shape's own footprint"} checked={params.add_base} onChange={(v) => set({ add_base: v })} />
        <SliderField id="base" label="Base thickness" unit=" mm" value={params.base_mm} min={0.4} max={10} step={0.1} disabled={!params.add_base} onChange={(v) => set({ base_mm: v })} />
      </Section>

      <Section title="Mesh">
        <SliderField id="resolution" label="Mesh resolution" unit=" px" value={params.resolution} min={50} max={500} step={10} onChange={(v) => set({ resolution: v })} />
        <SliderField id="smoothing" label="Smoothing" value={params.smoothing} min={0} max={5} step={0.1} onChange={(v) => set({ smoothing: v })} />
      </Section>

      <Section title="Transform">
        <div className="space-y-2">
          <p className="text-xs text-zinc-300">Rotation</p>
          <Segmented id="rotation" value={params.rotation_z} onChange={(v) => set({ rotation_z: v })} options={[0, 90, 180, 270].map((d) => ({ value: d, label: `${d}°` }))} />
        </div>
        <SwitchField id="upright" label="Stand upright" hint="Orient vertically (e.g. lithophane)" checked={params.stand_upright} onChange={(v) => set({ stand_upright: v })} />
        <SliderField id="scale" label="Scale" unit="×" value={params.scale} min={0.2} max={3} step={0.05} onChange={(v) => set({ scale: v })} />
      </Section>

      <Section title="Mounting">
        <SwitchField id="mounting-hole" label="Mounting hole" hint={params.add_base ? "Through-hole near the top edge" : "Requires a solid base"} checked={params.mounting_hole && params.add_base} disabled={!params.add_base} onChange={(v) => set({ mounting_hole: v })} />
        {params.mounting_hole && params.add_base && (
          <SliderField id="hole-diameter" label="Hole diameter" unit=" mm" value={params.hole_diameter_mm} min={2} max={12} step={0.5} onChange={(v) => set({ hole_diameter_mm: v })} />
        )}
      </Section>
    </div>
  );
};
